import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Button, TextField, Tooltip, Avatar,
} from '@mui/material';
import { Block, CheckCircle, Refresh } from '@mui/icons-material';
import { generateClient } from 'aws-amplify/api';
import { extractGraphQLError } from '../utils/graphqlError';

const client = generateClient();

const LIST_DRIVERS = `
  query ListAllDrivers($limit: Int, $nextToken: String) {
    listAllDrivers(limit: $limit, nextToken: $nextToken) {
      items { id phone name email status createdAt }
      nextToken
    }
  }
`;

const LIST_RIDERS = `
  query ListAllUsers($limit: Int, $nextToken: String) {
    listAllUsers(limit: $limit, nextToken: $nextToken) {
      items { id phone name email userType status createdAt }
      nextToken
    }
  }
`;

const SUSPEND_DRIVER = `
  mutation SuspendDriver($driverId: ID!, $reason: String!) {
    suspendDriver(driverId: $driverId, reason: $reason) { id status }
  }
`;

const REACTIVATE_DRIVER = `
  mutation ReactivateDriver($driverId: ID!) {
    reactivateDriver(driverId: $driverId) { id status }
  }
`;

const SUSPEND_RIDER = `
  mutation SuspendRider($riderId: ID!, $reason: String!) {
    suspendRider(riderId: $riderId, reason: $reason) { success message }
  }
`;

const REACTIVATE_RIDER = `
  mutation ReactivateRider($riderId: ID!) {
    reactivateRider(riderId: $riderId) { success message }
  }
`;

interface User { id: string; phone: string; name?: string; email?: string; status?: string; userType?: string; createdAt?: string; }

function statusChip(status?: string) {
  if (!status) return <Chip label="—" size="small" />;
  const color = status === 'SUSPENDED' ? 'error' : status === 'ONLINE' ? 'success' : status === 'OFFLINE' ? 'default' : 'primary';
  return <Chip label={status} size="small" color={color as any} />;
}

interface ConfirmDialog {
  open: boolean;
  action: 'suspend' | 'reactivate';
  user: User | null;
  reason: string;
}

export default function UserManagement() {
  const [tab, setTab] = useState(0);
  const [drivers, setDrivers] = useState<User[]>([]);
  const [riders, setRiders] = useState<User[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingRiders, setLoadingRiders] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ConfirmDialog>({ open: false, action: 'suspend', user: null, reason: '' });

  const loadDrivers = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const result: any = await client.graphql({ query: LIST_DRIVERS, variables: { limit: 100 } });
      setDrivers(result.data?.listAllDrivers?.items ?? []);
    } catch (e: any) { setError(extractGraphQLError(e, 'Failed to load drivers')); }
    finally { setLoadingDrivers(false); }
  }, []);

  const loadRiders = useCallback(async () => {
    setLoadingRiders(true);
    try {
      const result: any = await client.graphql({ query: LIST_RIDERS, variables: { limit: 100 } });
      setRiders(result.data?.listAllUsers?.items ?? []);
    } catch (e: any) { setError(extractGraphQLError(e, 'Failed to load riders')); }
    finally { setLoadingRiders(false); }
  }, []);

  useEffect(() => { loadDrivers(); loadRiders(); }, []);

  const openDialog = (action: 'suspend' | 'reactivate', user: User) =>
    setDialog({ open: true, action, user, reason: '' });

  const closeDialog = () => setDialog(d => ({ ...d, open: false, reason: '' }));

  const handleConfirm = async () => {
    const { action, user, reason } = dialog;
    if (!user) return;
    setActionLoading(user.id);
    closeDialog();
    try {
      const isDriver = tab === 0;
      if (action === 'suspend') {
        if (isDriver) {
          await client.graphql({ query: SUSPEND_DRIVER, variables: { driverId: user.id, reason } });
          setDrivers(ds => ds.map(d => d.id === user.id ? { ...d, status: 'SUSPENDED' } : d));
        } else {
          await client.graphql({ query: SUSPEND_RIDER, variables: { riderId: user.id, reason } });
          setRiders(rs => rs.map(r => r.id === user.id ? { ...r, status: 'SUSPENDED' } : r));
        }
      } else {
        if (isDriver) {
          await client.graphql({ query: REACTIVATE_DRIVER, variables: { driverId: user.id } });
          // map_driver shows driverStatus when not suspended — driver will be OFFLINE after reactivation
          setDrivers(ds => ds.map(d => d.id === user.id ? { ...d, status: 'OFFLINE' } : d));
        } else {
          await client.graphql({ query: REACTIVATE_RIDER, variables: { riderId: user.id } });
          setRiders(rs => rs.map(r => r.id === user.id ? { ...r, status: 'ACTIVE' } : r));
        }
      }
    } catch (e: any) { setError(extractGraphQLError(e, 'Failed to update account status')); }
    finally { setActionLoading(null); }
  };

  const UserTable = ({ users, loading, onRefresh }: { users: User[]; loading: boolean; onRefresh: () => void }) => (
    <Box>
      <Box display="flex" justifyContent="flex-end" mb={1}>
        <Tooltip title="Refresh"><IconButton onClick={onRefresh} size="small"><Refresh /></IconButton></Tooltip>
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell>User</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No users found</TableCell></TableRow>
              )}
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>{(u.name || u.phone || '?')[0].toUpperCase()}</Avatar>
                      <Box>
                        <Typography variant="body2">{u.name || '—'}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>{u.id.slice(0, 8)}…</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{u.phone}</TableCell>
                  <TableCell>{u.email || '—'}</TableCell>
                  <TableCell>{statusChip(u.status)}</TableCell>
                  <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align="center">
                    {actionLoading === u.id ? (
                      <CircularProgress size={18} />
                    ) : u.status === 'SUSPENDED' ? (
                      <Tooltip title="Reactivate account">
                        <IconButton size="small" color="success" onClick={() => openDialog('reactivate', u)}>
                          <CheckCircle fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Suspend account">
                        <IconButton size="small" color="error" onClick={() => openDialog('suspend', u)}>
                          <Block fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>User Management</Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Suspend or reactivate accounts. Suspension disables login and revokes all active sessions immediately.
        Data is read from DynamoDB — drivers and riders are stored in separate tables.
      </Typography>

      {error && <Alert severity={error.includes('permission') ? 'warning' : 'error'} sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`Drivers (${drivers.length})`} />
          <Tab label={`Riders (${riders.length})`} />
        </Tabs>
        <Box p={2}>
          {tab === 0 && <UserTable users={drivers} loading={loadingDrivers} onRefresh={loadDrivers} />}
          {tab === 1 && <UserTable users={riders} loading={loadingRiders} onRefresh={loadRiders} />}
        </Box>
      </Paper>

      {/* Confirm dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          {dialog.action === 'suspend' ? '⛔ Suspend Account' : '✅ Reactivate Account'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialog.action === 'suspend'
              ? `Suspending ${dialog.user?.name || dialog.user?.phone} will immediately revoke their session and prevent login.`
              : `Reactivating ${dialog.user?.name || dialog.user?.phone} will restore their access.`}
          </DialogContentText>
          {dialog.action === 'suspend' && (
            <TextField
              autoFocus fullWidth label="Reason (required)" size="small"
              value={dialog.reason}
              onChange={e => setDialog(d => ({ ...d, reason: e.target.value }))}
              placeholder="e.g. Violation of terms of service"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={dialog.action === 'suspend' ? 'error' : 'success'}
            onClick={handleConfirm}
            disabled={dialog.action === 'suspend' && !dialog.reason.trim()}
          >
            {dialog.action === 'suspend' ? 'Suspend' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
